import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { useCartStore } from '../../stores/cart.store';
import { LogOut, Menu, ShoppingCart, X } from 'lucide-react';
import { useUnreadNotificationsCount } from '../../hooks/use-notifications';
import { ChargementPage } from './lazyPage';
import { useMaFichePhotographe } from '../../hooks/use-photographe';
import BarreNavigationMobile, { barreMobileVisible } from './BarreNavigationMobile';

function MainLayout() {
  const { isAuthenticated, user, logout } = useAuthStore();
  const { getTotalItems } = useCartStore();
  const navigate = useNavigate();
  const cartItemsCount = getTotalItems();
  const { data: unreadNotifs = 0 } = useUnreadNotificationsCount(isAuthenticated);

  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Un changement de page doit refermer le tiroir : sinon il resterait ouvert
  // par-dessus la page d'arrivee.
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Le tiroir couvre l'ecran : laisser la page defiler derriere donne une
  // impression de double scroll.
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  /**
   * Un supporter ne gère ni match ni séance.
   *
   * « Matchs » et « Entraînements » ouvrent des écrans de gestion : il n'y
   * trouverait rien qui le concerne. Ce qu'il suit lui arrive par son fil.
   */
  const isAmateur = user?.user_type === 'amateur';

  /**
   * Le photographe a son propre parcours : son tableau de bord, le fil où il
   * publie, et sa page publique, celle qu'il partage après chaque match.
   */
  const isPhotographer = isAuthenticated && user?.user_type === 'photographer';
  const { data: fichePhotographe } = useMaFichePhotographe(isPhotographer);

  // Une seule source pour les deux navigations : la barre large et le tiroir
  // mobile ne peuvent pas diverger.
  const navLinks = isPhotographer
    ? [
        { to: '/photographe', label: 'Tableau de bord' },
        { to: '/feed', label: "Fil d'actualité" },
        ...(fichePhotographe ? [{ to: `/photographes/${fichePhotographe.slug}`, label: 'Ma page publique' }] : []),
      ]
    : [
    { to: '/feed', label: "Fil d'actualité" },
    ...(isAmateur ? [{ to: '/suivre', label: 'Découvrir' }] : []),
    { to: '/programmes', label: 'Programmes' },
    { to: '/jeux', label: 'Jeux' },
    { to: '/marketplace', label: 'Marketplace' },
    { to: '/coaches', label: 'Coachs' },
   // ...(isAmateur ? [] : [{ to: '/matches', label: 'Matchs' }]),
    ...(isAuthenticated && !isAmateur && user?.user_type !== 'club'
      ? [{ to: '/trainings', label: 'Entraînements' }]
      : []),
      ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      {/* Sur téléphone et tablette, les pages qui ont la barre du bas se passent
          de celle du haut, comme une application : le menu complet s'ouvre
          depuis l'onglet « Menu » en bas. */}
      <header
        className={`sticky top-0 z-50 bg-white border-b border-gray-200 backdrop-blur-sm bg-white/95 ${
          barreMobileVisible(location.pathname) ? 'hidden lg:block' : ''
        }`}
      >
        <div className="container-app">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <a href="/feed" className="shrink-0">
              <img src="/brand/fconnect-logo-horizontal.svg" alt="FCONNECT" className="h-9 w-auto" />
            </a>

            {/* Navigation : la barre complète demande ~1000 px avec les icônes et
                le nom du compte. En dessous (tablette en portrait), elle poussait
                tout l'écran hors champ et le navigateur dézoomait la page : on
                garde le tiroir jusqu'à 1024 px. */}
            <nav className="hidden min-w-0 items-center gap-0.5 lg:flex">
              {navLinks.map((link) => (
                <a
                  key={link.to}
                  href={link.to}
                  className="whitespace-nowrap px-2.5 xl:px-4 py-2 text-sm font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            {/* Right side */}
            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <button
                onClick={() => setIsMenuOpen(true)}
                aria-label="Ouvrir le menu"
                aria-expanded={isMenuOpen}
                className="rounded-lg p-2 text-gray-700 hover:bg-gray-100 lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
              {isAuthenticated ? (
                <>
                  {/* Cart */}
                  <button
                    onClick={() => navigate('/cart')}
                    className="relative p-2 text-gray-600 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                    title="Mon panier"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    {cartItemsCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-orange-500 to-orange-600 text-white text-xs font-bold rounded-full flex items-center justify-center ring-2 ring-white shadow-lg animate-pulse">
                        {cartItemsCount > 9 ? '9+' : cartItemsCount}
                      </span>
                    )}
                  </button>

                  {/* Notifications */}
                  <button
                    onClick={() => navigate('/notifications')}
                    className="relative p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Notifications"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    {unreadNotifs > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-white shadow">
                        {unreadNotifs > 9 ? '9+' : unreadNotifs}
                      </span>
                    )}
                  </button>

                  {/* Messages */}
                  <button 
                    onClick={() => navigate('/messages')}
                    className="relative p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                    title="Mes messages"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </button>

                  {/* User menu */}
                  <div className="hidden items-center gap-2 border-l pl-3 sm:flex">
                    <a href="/dashboard" className="flex items-center gap-2.5 cursor-pointer group hover:bg-gray-50 px-2 py-1.5 rounded-lg transition-colors">
                      {user?.avatar_url ? (
                        <img
                          src={user.avatar_url}
                          alt={user.first_name || 'User'}
                          className="w-8 h-8 rounded-full object-cover ring-2 ring-gray-200 group-hover:ring-green-500 transition-all"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center text-white text-sm font-semibold ring-2 ring-gray-200 group-hover:ring-green-500 transition-all">
                          {user?.first_name?.[0]?.toUpperCase() || 'U'}
                        </div>
                      )}
                      <span className="text-sm font-medium text-gray-900 group-hover:text-green-600 transition-colors max-w-[120px] truncate">
                        {user?.first_name || 'Mon profil'}
                      </span>
                    </a>
                    <button 
                      onClick={handleLogout} 
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Déconnexion"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <a href="/login" className="hidden px-4 py-2 text-sm font-medium text-gray-700 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors sm:inline-block">
                    Connexion
                  </a>
                  <a href="/register" className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 rounded-lg shadow-sm hover:shadow-md transition-all">
                    S'inscrire
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ---------------- Menu mobile ---------------- */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsMenuOpen(false)}
            aria-hidden="true"
          />

          <div className="absolute right-0 top-0 flex h-full w-[85%] max-w-xs flex-col bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <span className="text-sm font-bold text-gray-900">Menu</span>
              <button
                onClick={() => setIsMenuOpen(false)}
                aria-label="Fermer le menu"
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {isAuthenticated && (
              <a
                href="/dashboard"
                className="flex items-center gap-3 border-b px-5 py-4 hover:bg-gray-50"
              >
                {user?.avatar_url ? (
                  <img
                    src={user.avatar_url}
                    alt={user.first_name || 'Profil'}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-green-700 text-sm font-semibold text-white">
                    {user?.first_name?.[0]?.toUpperCase() || 'U'}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-gray-900">
                    {user?.first_name || 'Mon profil'}
                  </p>
                  <p className="truncate text-xs capitalize text-gray-500">{user?.user_type}</p>
                </div>
              </a>
            )}

            <nav className="flex-1 overflow-y-auto p-3">
              {navLinks.map((link) => (
                <a
                  key={link.to}
                  href={link.to}
                  className="block rounded-xl px-4 py-3 text-[15px] font-medium text-gray-700 hover:bg-green-50 hover:text-green-700"
                >
                  {link.label}
                </a>
              ))}

              {isAuthenticated && (
                <>
                  <div className="my-2 border-t" />
                  <a
                    href="/messages"
                    className="block rounded-xl px-4 py-3 text-[15px] font-medium text-gray-700 hover:bg-green-50 hover:text-green-700"
                  >
                    Messages
                  </a>
                  <a
                    href="/notifications"
                    className="flex items-center justify-between rounded-xl px-4 py-3 text-[15px] font-medium text-gray-700 hover:bg-green-50 hover:text-green-700"
                  >
                    Notifications
                    {unreadNotifs > 0 && (
                      <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold text-white">
                        {unreadNotifs}
                      </span>
                    )}
                  </a>
                  <a
                    href="/cart"
                    className="flex items-center justify-between rounded-xl px-4 py-3 text-[15px] font-medium text-gray-700 hover:bg-green-50 hover:text-green-700"
                  >
                    Panier
                    {cartItemsCount > 0 && (
                      <span className="rounded-full bg-green-600 px-2 py-0.5 text-xs font-bold text-white">
                        {cartItemsCount}
                      </span>
                    )}
                  </a>
                </>
              )}
            </nav>

            <div className="border-t p-3">
              {isAuthenticated ? (
                <button
                  onClick={handleLogout}
                  className="flex w-full items-center gap-2 rounded-xl px-4 py-3 text-[15px] font-medium text-red-600 hover:bg-red-50"
                >
                  <LogOut className="h-4 w-4" /> Deconnexion
                </button>
              ) : (
                <div className="space-y-2">
                  <a
                    href="/login"
                    className="block rounded-xl border border-gray-200 px-4 py-3 text-center text-[15px] font-medium text-gray-700"
                  >
                    Connexion
                  </a>
                  <a
                    href="/register"
                    className="block rounded-xl bg-gradient-to-r from-green-600 to-green-700 px-4 py-3 text-center text-[15px] font-semibold text-white"
                  >
                    S'inscrire
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main>
        <Suspense fallback={<ChargementPage />}>
          <Outlet />
        </Suspense>
      </main>

      {/* Footer */}
      <footer className="mt-auto bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>

        <div className="container-app relative">
          {/* Main Footer Content */}
          <div className="py-12 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
            {/* Brand Column */}
            <div className="lg:col-span-1">
              <div className="mb-4">
                <img src="/brand/fconnect-logo-horizontal-blanc.svg" alt="FCONNECT" className="h-12 w-auto" />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                La plateforme qui connecte l'écosystème du football amateur en Afrique de l'Ouest
              </p>
              {/* Social Links */}
              <div className="flex gap-3">
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-all hover:scale-110" title="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-all hover:scale-110" title="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-all hover:scale-110" title="Twitter">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                  </svg>
                </a>
                <a href="#" className="w-10 h-10 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-all hover:scale-110" title="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Plateforme</h4>
              <ul className="space-y-3">
                <li>
                  <a href="/feed" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Fil d'actualité
                  </a>
                </li>
                <li>
                  <a href="/marketplace" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Marketplace
                  </a>
                </li>
                <li>
                  <a href="/coaches" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Coachs
                  </a>
                </li>
                <li>
                  <a href="/matches" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Matchs
                  </a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Entreprise</h4>
              <ul className="space-y-3">
                <li>
                  <a href="/about" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    À propos
                  </a>
                </li>
                <li>
                  <a href="/contact" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Contact
                  </a>
                </li>
                <li>
                  <a href="/careers" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Carrières
                  </a>
                </li>
                <li>
                  <a href="/blog" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Blog
                  </a>
                </li>
              </ul>
            </div>

            {/* Support */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Support</h4>
              <ul className="space-y-3">
                <li>
                  <a href="/help" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Centre d'aide
                  </a>
                </li>
                <li>
                  <a href="/faq" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    FAQ
                  </a>
                </li>
                <li>
                  <a href="/cgu" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    CGU
                  </a>
                </li>
                <li>
                  <a href="/privacy" className="text-gray-400 hover:text-green-400 transition-colors text-sm flex items-center group">
                    <svg className="w-4 h-4 mr-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    Confidentialité
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-white/10 py-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © 2025 FCONNECT. Tous droits réservés.
              </p>
              <div className="flex items-center gap-6 text-sm">
                <a href="/terms" className="text-gray-400 hover:text-green-400 transition-colors">
                  Conditions
                </a>
                <a href="/privacy" className="text-gray-400 hover:text-green-400 transition-colors">
                  Confidentialité
                </a>
                <a href="/cookies" className="text-gray-400 hover:text-green-400 transition-colors">
                  Cookies
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Barre du bas, façon application, sur téléphone et tablette. */}
      <BarreNavigationMobile
        notificationsNonLues={unreadNotifs}
        onOuvrirMenu={() => setIsMenuOpen(true)}
      />
    </div>
  );
}

export default MainLayout;
